import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { OrderBook } from '../proto/generated/security'
import { Snapshot } from '../reducer/security.reducer'
import { RootState } from '../store'
import { getSecurityKey } from '../util/security'
import {
    orderBookBidAskTickEquals,
    orderBookDepthTickEquals,
    orderBookOverviewTickEquals,
    orderBooksRecordTickEquals,
    orderBookTickEquals,
    snapshotTickEquals,
    snapshotsRecordTickEquals,
} from '../util/snapshotTick'

type SecurityRef = { exchange: number; code: string } | undefined | null

export function securityKeysFromRefs(refs: SecurityRef[]): string[] {
    const keys = new Set<string>()
    for (const s of refs) {
        if (s?.code) keys.add(getSecurityKey(s.exchange, s.code))
    }
    return Array.from(keys)
}

export function useSnapshotTick(securityKey: string) {
    return useSelector(
        (state: RootState) => (securityKey ? state.securityStore.snapshots[securityKey] : undefined),
        snapshotTickEquals
    )
}

export function useOrderBookTick(securityKey: string, bidAsk = false) {
    const equals = bidAsk ? orderBookBidAskTickEquals : orderBookTickEquals
    return useSelector(
        (state: RootState) => (securityKey ? state.securityStore.orderBooks[securityKey] : undefined),
        equals
    )
}

export function useOrderBookDepthTick(securityKey: string) {
    return useSelector(
        (state: RootState) => (securityKey ? state.securityStore.orderBooks[securityKey] : undefined),
        orderBookDepthTickEquals
    )
}

export function useOrderBookOverviewTick(securityKey: string) {
    return useSelector(
        (state: RootState) => (securityKey ? state.securityStore.orderBooks[securityKey] : undefined),
        orderBookOverviewTickEquals
    )
}

type DepthSide = 'bid' | 'ask'
type DepthQuoteField = 'price' | 'quantity' | 'numberOfOrders'

const getQuotes = (book: OrderBook | undefined, side: DepthSide) =>
    side === 'bid' ? book?.bids : book?.asks

export function useOrderBookQuoteCount(securityKey: string, side: DepthSide) {
    return useSelector(
        (state: RootState) => {
            const quotes = getQuotes(state.securityStore.orderBooks[securityKey], side)
            return quotes?.length ?? 0
        },
        (a, b) => a === b
    )
}

export function useOrderBookQuoteField(
    securityKey: string,
    side: DepthSide,
    index: number,
    field: DepthQuoteField
) {
    return useSelector(
        (state: RootState) => getQuotes(state.securityStore.orderBooks[securityKey], side)?.[index]?.[field],
        (a, b) => a === b
    )
}

export function useOrderBookSideMaxQuantity(securityKey: string, side: DepthSide) {
    return useSelector(
        (state: RootState) => {
            const quotes = getQuotes(state.securityStore.orderBooks[securityKey], side)
            if (!quotes?.length) return 0
            let max = 0
            for (const quote of quotes) {
                if (quote.quantity > max) max = quote.quantity
            }
            return max
        },
        (a, b) => a === b
    )
}

export function useOrderBookSideTotal(securityKey: string, side: DepthSide) {
    return useSelector(
        (state: RootState) => {
            const book = state.securityStore.orderBooks[securityKey]
            if (!book) return 0
            return side === 'bid' ? book.totalBuyQuantity : book.totalSellQuantity
        },
        (a, b) => a === b
    )
}

export function useHasOrderBook(securityKey: string) {
    return useSelector(
        (state: RootState) => !!state.securityStore.orderBooks[securityKey],
        (a, b) => a === b
    )
}

export function useSnapshotsTick(securityKeys: string[]) {
    const keysKey = securityKeys.join('\0')
    const keys = useMemo(() => securityKeys, [keysKey])

    return useSelector((state: RootState): Record<string, Snapshot> | null => {
        if (!keys.length) return null
        const all = state.securityStore.snapshots
        const result: Record<string, Snapshot> = {}
        for (const key of keys) {
            if (all[key]) result[key] = all[key]
        }
        return result
    }, snapshotsRecordTickEquals)
}

export function useOrderBooksTick(securityKeys: string[], bidAsk = true) {
    const keysKey = securityKeys.join('\0')
    const keys = useMemo(() => securityKeys, [keysKey])
    const equals = bidAsk ? orderBookBidAskTickEquals : orderBookTickEquals

    return useSelector((state: RootState): Record<string, OrderBook> | null => {
        if (!keys.length) return null
        const all = state.securityStore.orderBooks
        const result: Record<string, OrderBook> = {}
        for (const key of keys) {
            if (all[key]) result[key] = all[key]
        }
        return result
    }, (a, b) => orderBooksRecordTickEquals(a, b, equals))
}
